// The final outcome of a bulk import, read from GET /tasks/{id}, and the
// i18n lines that explain it. Pure, so the banner, the toast and the tests
// all run the same branches.
//
// Once status is "done" the server's counts add up to `total`:
// imported + already_saved + unreachable + failed + not_imported.

/** A task the app was tracking disappeared (API restart, or result expired). */
export const LOST_IMPORT = Object.freeze({ lost: true })

/** A count from an import task answer: missing, negative or non-numeric reads as 0. */
export const count = (value) => (Number.isFinite(value) && value > 0 ? value : 0)

/**
 * The summary of a finished import, or null when the response is not a
 * finished one. An older backend without `status` also gives null, so the
 * app keeps its old silent completion instead of showing made-up numbers.
 */
export function importSummaryFromTask(task) {
  if (!task || task.status !== 'done') return null
  return {
    total: count(task.total),
    imported: count(task.imported),
    alreadySaved: count(task.already_saved),
    unreachable: count(task.unreachable),
    failed: count(task.failed),
    notImported: count(task.not_imported),
    stoppedBy: task.stopped_by === 'weekly_limit' || task.stopped_by === 'bookmark_limit' ? task.stopped_by : null,
    limit: count(task.limit)
  }
}

/** Nothing the user asked for is missing: every link landed or was already there. */
export function isCleanImport(summary) {
  if (!summary || summary.lost) return false
  return summary.total > 0 && summary.unreachable + summary.failed + summary.notImported === 0
}

/**
 * What to show for a summary: a title and the lines that account for every
 * link that was not imported. Each entry is an i18n key under
 * `importData.summary` plus the numbers it needs; `count` picks the plural.
 */
export function importSummaryMessages(summary) {
  if (!summary) return null
  if (summary.lost) {
    return { title: { key: 'lostTitle' }, lines: [{ key: 'lost' }], upgrade: false }
  }

  const { total, imported } = summary
  let title
  if (total === 0) title = { key: 'title.empty' }
  else if (imported === 0) title = { key: 'title.none' }
  else if (imported === total) title = { key: 'title.all', params: { n: imported }, count: imported }
  else title = { key: 'title.partial', params: { imported, total }, count: imported }

  const lines = []
  const add = (key, n, params = {}) => {
    if (n > 0) lines.push({ key, params: { n, ...params }, count: n })
  }
  if (summary.stoppedBy === 'bookmark_limit') add('bookmarkLimit', summary.notImported, { limit: summary.limit })
  else if (summary.stoppedBy === 'weekly_limit') add('weeklyLimit', summary.notImported, { limit: summary.limit })
  else add('notImported', summary.notImported)
  add('unreachable', summary.unreachable)
  add('failed', summary.failed)
  add('alreadySaved', summary.alreadySaved)

  return { title, lines, upgrade: summary.stoppedBy === 'bookmark_limit' && summary.notImported > 0 }
}

/**
 * One title or line from importSummaryMessages (or, with its own `prefix`,
 * importLimitWarningMessages) as display text, with its numbers in the
 * reader's locale ("1,000" / "1.000").
 */
export function formatImportSummaryLine(line, t, formatNumber, prefix = 'importData.summary') {
  const params = {}
  for (const [name, value] of Object.entries(line.params || {})) {
    params[name] = typeof value === 'number' ? formatNumber(value) : value
  }
  const key = `${prefix}.${line.key}`
  return line.count === undefined ? t(key, params) : t(key, params, line.count)
}
